import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import {
  isPushNotificationSupported,
  getNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  isSubscribedToPushNotifications,
  showLocalNotification
} from '../../../services/pushNotificationService';
import { useAuth } from '../../../contexts/AuthContext';

const PushNotificationSettings = () => {
  const { user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    checkSupport();
    checkSubscription();
  }, [user]);

  const checkSupport = () => {
    const supported = isPushNotificationSupported();
    setIsSupported(supported);
    if (supported) {
      setPermission(getNotificationPermission());
    }
  };

  const checkSubscription = async () => {
    if (!user) return;
    const subscribed = await isSubscribedToPushNotifications(user?.id);
    setIsSubscribed(subscribed);
  };

  const handleEnableNotifications = async () => {
    if (!user) return;

    setLoading(true);
    setMessage(null);

    try {
      const result = await subscribeToPushNotifications(user?.id);
      
      if (result?.success) {
        setIsSubscribed(true);
        setPermission('granted');
        setMessage({ type: 'success', text: 'Push notifications enabled successfully!' });
        
        // Show a test notification
        setTimeout(() => {
          showLocalNotification('Push Notifications Enabled', {
            body: 'You will now receive real-time updates for predictions, results, and more!',
            icon: '/favicon.ico'
          });
        }, 500);
      } else {
        setMessage({ type: 'error', text: result?.error || 'Failed to enable notifications' });
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    if (!user) return;

    setLoading(true);
    setMessage(null);

    try {
      const result = await unsubscribeFromPushNotifications(user?.id);
      
      if (result?.success) {
        setIsSubscribed(false);
        setMessage({ type: 'success', text: 'Push notifications disabled' });
      } else {
        setMessage({ type: 'error', text: result?.error || 'Failed to disable notifications' });
      }
    } catch (error) {
      console.error('Error disabling notifications:', error);
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async () => {
    const result = await showLocalNotification('Test Notification', {
      body: 'This is a test notification from PowerPlay Amoeboids!',
      icon: '/favicon.ico',
      tag: 'test'
    });

    if (!result?.success) {
      setMessage({ type: 'error', text: result?.error || 'Failed to show test notification' });
    }
  };

  if (!isSupported) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-sm p-6 border border-border">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="BellOff" size={20} className="text-muted-foreground" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Push Notifications Not Supported
            </h3>
            <p className="text-sm text-muted-foreground">
              Your browser doesn't support push notifications. Please use a modern browser like Chrome, Firefox, or Edge.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-sm p-6 border border-border">
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          isSubscribed ? 'bg-success/10' : 'bg-muted'
        }`}>
          <Icon 
            name={isSubscribed ? "Bell" : "BellOff"} 
            size={20} 
            className={isSubscribed ? 'text-success' : 'text-muted-foreground'} 
          />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Push Notifications
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {isSubscribed 
              ? 'You are receiving real-time push notifications for prediction deadlines, match results, leaderboard changes, and tournament announcements.'
              : 'Enable push notifications to receive real-time updates even when you\'re not on the site.'}
          </p>

          {/* Status */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-medium text-foreground">Status:</span>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              isSubscribed 
                ? 'bg-success/10 text-success'
                : permission === 'denied' ?'bg-destructive/10 text-destructive' :'bg-muted text-muted-foreground'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                isSubscribed ? 'bg-success' : permission === 'denied' ? 'bg-destructive' : 'bg-muted-foreground'
              }`} />
              {isSubscribed ? 'Enabled' : permission === 'denied' ? 'Blocked' : 'Disabled'}
            </span>
          </div>

          {/* Message */}
          {message && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              message?.type === 'success' ?'bg-success/10 text-success border border-success/20' :'bg-destructive/10 text-destructive border border-destructive/20'
            }`}>
              {message?.text}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3">
            {!isSubscribed ? (
              <Button
                onClick={handleEnableNotifications}
                disabled={loading || permission === 'denied'}
                className="flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Enabling...
                  </>
                ) : (
                  <>
                    <Icon name="Bell" size={16} />
                    Enable Notifications
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleDisableNotifications}
                  disabled={loading}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                      Disabling...
                    </>
                  ) : (
                    <>
                      <Icon name="BellOff" size={16} />
                      Disable
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleTestNotification}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Icon name="Send" size={16} />
                  Test Notification
                </Button>
              </>
            )}
          </div>

          {/* Permission Denied Help */}
          {permission === 'denied' && (
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">
                <strong>Notifications are blocked.</strong> To enable them:
              </p>
              <ol className="text-sm text-muted-foreground space-y-1 ml-4 list-decimal">
                <li>Click the lock icon in your browser's address bar</li>
                <li>Find "Notifications" in the permissions list</li>
                <li>Change the setting to "Allow"</li>
                <li>Refresh this page and try again</li>
              </ol>
            </div>
          )}

          {/* Features List */}
          {!isSubscribed && permission !== 'denied' && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-sm font-medium text-foreground mb-2">You'll receive notifications for:</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Icon name="Clock" size={16} className="mt-0.5 flex-shrink-0" />
                  <span>Prediction deadlines approaching</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Icon name="Trophy" size={16} className="mt-0.5 flex-shrink-0" />
                  <span>Match results and your prediction performance</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Icon name="TrendingUp" size={16} className="mt-0.5 flex-shrink-0" />
                  <span>Leaderboard rank changes</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Icon name="Megaphone" size={16} className="mt-0.5 flex-shrink-0" />
                  <span>Tournament announcements and updates</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PushNotificationSettings;